'use client'

/**
 * Pricing Page
 *
 * Displays subscription options with feature comparison.
 * Monthly ($7.99) and Yearly ($47.99) premium plans.
 * Free tier with usage limits.
 */

import { useState, useRef, useEffect, useLayoutEffect, type MouseEvent } from 'react'
import Link from 'next/link'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { useAuth } from '@/contexts/AuthContext'
import { useDefaultLawn } from '@/hooks/use-default-lawn'
import { mapGrassTypeToLawnProfile } from '@/lib/grass-type-mapping'
import { Button } from '@/components/ui/button'
import { analytics, trackEvent } from '@/lib/analytics'
import {
  closeNavigationTarget,
  navigateToResolvedUrl,
  openModifiedClickTarget,
} from '@/lib/navigation/modified-click'
import { FaqLeafChevron } from '@/components/home/section-icons'
import { Reveal, Stagger, HoverLift } from '@/components/motion'
import { PremiumTrialTimeline } from '@/components/billing/premium-trial-timeline'
import { ResponsiveTable } from '@/components/ui/responsive-table'
import { FREE_LIMITS, PREMIUM_TRIAL_DAYS, formatLimit, isUnlimited } from '@/lib/auth/feature-flags'
import { WEB_DISCOUNT, WEB_DISCOUNT_REASON, PREMIUM_TRANSFERS_TO_APP } from '@/lib/pricing-display'
import {
  Check,
  X as XIcon,
  Minus,
  Shield,
  Loader2,
  RotateCcw,
  CreditCard,
  Crown,
  Zap,
  Leaf,
} from 'lucide-react'

type BillingInterval = 'monthly' | 'yearly'

// useLayoutEffect reads DOM geometry before paint (so the toggle thumb never
// flashes unpositioned); fall back to useEffect on the server to avoid React's
// SSR warning in this client component.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Every Premium CTA on this page opens Stripe checkout, which attaches the
// web-direct coupon. So the page quotes the web price throughout, and shows the
// App Store price only as the anchor it is being compared against.
const MONTHLY_PRICE = WEB_DISCOUNT.monthly.web
const YEARLY_PRICE = WEB_DISCOUNT.yearly.web
const YEARLY_SAVINGS = MONTHLY_PRICE * 12 - YEARLY_PRICE

const FREE_FEATURES = [
  `${formatLimit(FREE_LIMITS.ANALYSES_PER_MONTH)} grass IDs + 3 shared weed IDs or lawn diagnoses per month`,
  `${formatLimit(FREE_LIMITS.CARE_PLANS_PER_MONTH)} care plan per month`,
  isUnlimited(FREE_LIMITS.SAVED_LAWNS)
    ? 'Unlimited saved lawn profiles'
    : `${formatLimit(FREE_LIMITS.SAVED_LAWNS)} saved lawn profile`,
  'Care plan PDF downloads',
  `${formatLimit(FREE_LIMITS.HISTORY_DAYS)} days of analysis history`,
  'Basic lawn care calculators',
  'Access to blog & guides',
] as const

const PREMIUM_FEATURES = [
  { text: 'Smart seasonal reminders timed to your zip + grass', highlight: true },
  { text: 'Unlimited personalized care plans', highlight: true },
  { text: 'Unlimited grass IDs, weed IDs, and lawn diagnoses', highlight: true },
  { text: 'Deeper scans: up to 5 photos and unlimited refinements', highlight: false },
  { text: 'Full lawn health and analysis history', highlight: false },
  { text: 'Saved soil tests + annual re-test reminders', highlight: false },
] as const

// Helper components mirror the homepage PricingPreview styling so the
// pricing card section feels unified with the homepage section the user
// scrolled in from. Anything outside the cards (value bridge, comparison,
// FAQ, final CTA) is /pricing-only and keeps its own treatment below.
function PopularBadge() {
  return (
    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-md">
        <Crown className="w-3 h-3" aria-hidden="true" />
        Most popular
      </span>
    </div>
  )
}

function TrustBand() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-6 text-sm text-gray-600">
      <span className="font-semibold text-gray-900">Plans for all 50 states</span>
      <span aria-hidden="true" className="text-gray-300">·</span>
      <span>Cancel anytime</span>
    </div>
  )
}

function BillingToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval
  onChange: (next: BillingInterval) => void
}) {
  const monthlyRef = useRef<HTMLButtonElement>(null)
  const yearlyRef = useRef<HTMLButtonElement>(null)
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null)
  // Slide transition is enabled one frame after the first measurement so the
  // thumb appears in place on load instead of animating in from the left.
  const [animate, setAnimate] = useState(false)

  useIsoLayoutEffect(() => {
    const measure = () => {
      const active = interval === 'monthly' ? monthlyRef.current : yearlyRef.current
      if (active) setThumb({ left: active.offsetLeft, width: active.offsetWidth })
    }
    measure()
    const raf = requestAnimationFrame(() => setAnimate(true))
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
    }
  }, [interval])

  const tabBase =
    'relative z-10 px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors min-h-[42px]'
  const tabActive = 'text-white dark:text-gray-900'
  const tabIdle = 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'

  return (
    <div className="flex justify-center mb-8 md:mb-10">
      <div
        role="tablist"
        aria-label="Billing interval"
        className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-1 rounded-full inline-flex shadow-sm"
      >
        {/* Sliding thumb — sits behind the tabs and tracks the active one */}
        {thumb && (
          <span
            aria-hidden="true"
            className={`absolute top-1 bottom-1 left-0 rounded-full bg-gray-900 dark:bg-gray-100 shadow-sm ${
              animate
                ? 'transition-[transform,width] duration-300 ease-[var(--ease-standard)] motion-reduce:transition-none'
                : ''
            }`}
            style={{ width: thumb.width, transform: `translateX(${thumb.left}px)` }}
          />
        )}
        <button
          ref={monthlyRef}
          type="button"
          role="tab"
          aria-selected={interval === 'monthly'}
          onClick={() => onChange('monthly')}
          className={`${tabBase} ${interval === 'monthly' ? tabActive : tabIdle}`}
        >
          Monthly
        </button>
        <button
          ref={yearlyRef}
          type="button"
          role="tab"
          aria-selected={interval === 'yearly'}
          onClick={() => onChange('yearly')}
          className={`${tabBase} inline-flex items-center gap-2 ${
            interval === 'yearly' ? tabActive : tabIdle
          }`}
        >
          Yearly
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${
              interval === 'yearly'
                ? 'bg-green-400/20 text-green-200 dark:bg-green-600/25 dark:text-green-800'
                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            }`}
          >
            Save ${YEARLY_SAVINGS.toFixed(0)}
          </span>
        </button>
      </div>
    </div>
  )
}

export function PricingPageContent() {
  const { user, subscription, isLoading: authIdentityLoading, subscriptionLoading } = useAuth()
  // `isLoading` from the context now means "identity resolved"; premium state
  // resolves separately, and this screen renders free-vs-premium UI, so it must
  // wait for both.
  const isLoading = authIdentityLoading || subscriptionLoading
  const { lawn } = useDefaultLawn()
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('yearly')
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)
  const [loadingPortal, setLoadingPortal] = useState(false)
  const pricingCardsRef = useRef<HTMLDivElement>(null)
  const promptShownRef = useRef(false)
  const isPremium = subscription?.isPremium === true
  const isYearly = billingInterval === 'yearly'

  // Endowment + specificity: for a logged-in user with a saved lawn, reaffirm
  // what they're upgrading. Empty for anonymous users (useDefaultLawn returns
  // no lawn), so the anonymous view is unchanged and there's no SSR mismatch.
  const rawGrass = lawn?.grass_type ? mapGrassTypeToLawnProfile(lawn.grass_type) : null
  const grassLabel = rawGrass ? rawGrass.replace(/\s+Grass$/i, '') : null
  const locationLabel =
    lawn?.city && lawn?.state ? `${lawn.city}, ${lawn.state}` : lawn?.zip_code || null
  const paymentTimingLabel = `No charge for ${PREMIUM_TRIAL_DAYS} days`
  const renewalPriceLabel = isYearly ? WEB_DISCOUNT.yearly.webDisplay : WEB_DISCOUNT.monthly.webDisplay

  useEffect(() => {
    if (promptShownRef.current) return
    analytics.upgradePromptShown('pricing_page', 0, 0)
    promptShownRef.current = true
  }, [])

  // Scroll-depth instrumentation — mirrors useBlogTracking pattern.
  // Fires pricing_scroll_depth at 25/50/75/100%, once per page view,
  // using rAF-throttling and passive listener for zero layout impact.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const fired = { 25: false, 50: false, 75: false, 100: false } as Record<number, boolean>

    const getDepth = (): number => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return 100
      return Math.min(100, Math.round((window.scrollY / scrollable) * 100))
    }

    const checkDepth = () => {
      const depth = getDepth()
      for (const threshold of [25, 50, 75, 100] as const) {
        if (!fired[threshold] && depth >= threshold) {
          fired[threshold] = true
          trackEvent('pricing_scroll_depth', { percent: threshold })
        }
      }
    }

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkDepth()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // Check initial position (e.g. page restored mid-scroll)
    checkDepth()

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  // Price block + CTA label — same shape as the homepage PricingPreview so
  // the dollar anchoring matches what the user just saw in the / section.
  // Both intervals lead with the trial ("Free / for N days") because the trial
  // is the actual offer ($0 today on either plan) — headlining it only on
  // monthly made the trial read as monthly-only. The yearly per-month anchor
  // ($4.00/mo) moves into the sub-line so we keep the value framing without
  // contradicting the trial headline; the "Save $X" toggle badge reinforces it.
  const premiumPlan = isYearly ? WEB_DISCOUNT.yearly : WEB_DISCOUNT.monthly
  const premiumPriceBlock = isYearly
    ? {
        big: 'Free',
        suffix: `for ${PREMIUM_TRIAL_DAYS} days`,
        sub: `Then $${YEARLY_PRICE}/year ($${(YEARLY_PRICE / 12).toFixed(2)}/mo). Cancel anytime.`,
        appStoreAnchor: WEB_DISCOUNT.yearly.appStoreDisplay,
      }
    : {
        big: 'Free',
        suffix: `for ${PREMIUM_TRIAL_DAYS} days`,
        sub: `Then $${MONTHLY_PRICE}/mo. Cancel anytime.`,
        appStoreAnchor: WEB_DISCOUNT.monthly.appStoreDisplay,
      }

  // Same unified OAuth page the header's LoginButton opens, returning here.
  const freeSignInHref = `/auth/login?redirect=${encodeURIComponent('/pricing')}`

  const premiumCtaLabel = grassLabel
    ? `Start my ${grassLabel} trial`
    : `Start my ${PREMIUM_TRIAL_DAYS}-day trial`

  const handleManageBilling = async (navigationTarget: Window | null) => {
    setLoadingPortal(true)
    try {
      const response = await fetch('/api/stripe/billing-portal', {
        method: 'POST',
      })
      const data = await response.json()

      if (data.url) {
        navigateToResolvedUrl(data.url, navigationTarget)
        return
      }

      closeNavigationTarget(navigationTarget)
      console.error('Failed to open billing portal:', data.error)
      alert('Failed to open billing portal. Please try again.')
    } catch (error) {
      closeNavigationTarget(navigationTarget)
      console.error('Billing portal error:', error)
      alert('An error occurred. Please try again.')
    } finally {
      setLoadingPortal(false)
    }
  }

  const handleSubscribe = async (tierName: string, event: MouseEvent<HTMLButtonElement>) => {
    const navigationTarget = openModifiedClickTarget(event)

    if (tierName === 'Free') {
      if (isPremium) {
        await handleManageBilling(navigationTarget)
        return
      }

      navigateToResolvedUrl('/', navigationTarget)
      return
    }

    if (isPremium) {
      await handleManageBilling(navigationTarget)
      return
    }

    setLoadingPlan(tierName)

    // Read the funnel attribution at click time instead of via useSearchParams:
    // that hook opts the whole tree out of static rendering, which left the
    // served HTML as a spinner with no crawlable plan copy.
    const attributionId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('funnel_id') || undefined
        : undefined

    // Track upgrade click
    analytics.upgradeClicked('pricing_page', billingInterval)

    // Use guest checkout for unauthenticated users, regular checkout for authenticated
    const endpoint = user
      ? '/api/stripe/create-checkout'
      : '/api/stripe/create-guest-checkout'

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: billingInterval,
          attributionId,
        }),
      })

      const data = await response.json()

      if (data.url) {
        // Track checkout initiation
        const amount = billingInterval === 'monthly'
          ? MONTHLY_PRICE
          : YEARLY_PRICE
        analytics.checkoutInitiated(billingInterval, amount)

        navigateToResolvedUrl(data.url, navigationTarget)
      } else if (data.code === 'already_subscribed') {
        // The user is entitled already, so checkout refused rather than billing
        // them a second time. Switching plans is a real intent that lands here
        // (monthly subscriber choosing yearly), so send them somewhere that can
        // actually do it instead of showing a generic failure.
        if (data.managedBy === 'app_store') {
          closeNavigationTarget(navigationTarget)
          alert(
            'Your Premium subscription is billed through the App Store. Change or cancel it in your iPhone Settings under Apple ID, Subscriptions.'
          )
        } else {
          await handleManageBilling(navigationTarget)
        }
      } else {
        closeNavigationTarget(navigationTarget)
        console.error('Failed to create checkout session:', data.error)
        alert('Failed to start checkout. Please try again.')
      }
    } catch (error) {
      closeNavigationTarget(navigationTarget)
      console.error('Checkout error:', error)
      alert('An error occurred. Please try again.')
    } finally {
      setLoadingPlan(null)
    }
  }

  // Only a signed-in non-premium user is actually ON the free plan. An
  // anonymous visitor has no plan, and showing them a dead "Current Plan" pill
  // removes the only free-path CTA on the page.
  const isCurrentFreePlan = !!user && !isPremium
  const freeButtonDisabled =
    isLoading || loadingPlan !== null || loadingPortal || isCurrentFreePlan || isPremium
  const premiumButtonDisabled = isLoading || loadingPlan !== null || loadingPortal
  const isCheckoutLoading = loadingPlan === 'Premium' || loadingPortal

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero + pricing cards share the signature green/amber wash with two
          blurred corner blobs (design `--grad-hero`). overflow-hidden clips the
          blobs so they never trigger horizontal scroll on mobile. */}
      <section className="relative overflow-hidden bg-linear-to-br from-green-50 via-white to-emerald-50">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-28 -top-52 h-[520px] w-[520px] rounded-full bg-green-300/40 blur-[80px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-60 -left-36 h-[460px] w-[460px] rounded-full bg-amber-400/15 blur-[80px]"
        />

        <div className="relative z-10">
      {/* Hero — mirrors homepage PricingPreview's hierarchy: trust band,
          headline, subtitle, billing toggle. Trust-pill row removed; the
          risk-reversal triplet now lives in-card below the Premium CTA. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="text-center">
          <Reveal>
            <TrustBand />
            <h1 className="font-poppins text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-gray-900 mb-4 leading-[1.05] tracking-[-0.03em]">
              Never miss a{' '}
              <span className="text-green-600 dark:text-green-400">treatment window</span>{' '}
              again
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Get reminders timed to your zip and grass type. Plus unlimited grass IDs, weed IDs, lawn diagnoses, and care plans.
            </p>
            {!isPremium && grassLabel && (
              <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-4 py-1.5 text-sm font-semibold text-green-800">
                <Leaf className="w-4 h-4 text-green-600" aria-hidden="true" />
                Premium for your {grassLabel} lawn{locationLabel ? ` in ${locationLabel}` : ''}
              </p>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.08} className="mt-10">
          <BillingToggle
            interval={billingInterval}
            onChange={(next) => {
              analytics.billingIntervalToggled(next)
              setBillingInterval(next)
            }}
          />
        </Reveal>
      </div>

      {/* Pricing cards — custom div pattern matching homepage so the visual
          continuity holds. Auth-aware button states preserved (Current Plan,
          Included in Premium, Manage subscription, loading portal). */}
      <div ref={pricingCardsRef} className="form-rail pb-16 scroll-mt-24">
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
          {/* Free tier — demoted: neutral surface, lighter border, neutral CTA */}
          <Stagger.Item>
            <HoverLift
              lift={-3}
              pressScale={1}
              className="relative rounded-[20px] bg-white/70 border border-gray-200 p-6 md:p-7 flex flex-col h-full"
            >
              <h2 className="text-lg font-bold text-gray-900">Free</h2>
              <p className="mt-1 text-xs text-gray-500">Try the basics, no card required</p>
              <div className="mt-3 flex items-baseline">
                <span className="text-[46px] leading-none font-extrabold text-gray-900">$0</span>
                <span className="ml-1.5 text-sm font-medium text-gray-500">forever</span>
              </div>
              {/* Reserve the same vertical space the Premium card uses for its
                  price sub-line so both card bodies start at the same Y. */}
              <div className="mt-1 min-h-[1rem]" aria-hidden="true" />

              <ul className="mt-5 space-y-2.5 flex-1">
                {FREE_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-4 text-xs text-gray-500 italic">
                Need unlimited scans, care plans, or smart reminders? See Premium →
              </p>

              {/* Free CTA — intentionally subordinate to Premium. Shown as a
                  muted text link so it stays available for prospects who want
                  the free path but doesn't compete with the Premium button.
                  Disabled state (Current Plan / Included in Premium) keeps a
                  minimal pill treatment so logged-in users can read their
                  current status at a glance. */}
              {isCurrentFreePlan || isPremium ? (
                <div className="mt-6 inline-flex w-full items-center justify-center min-h-[44px] px-5 py-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 font-medium text-sm cursor-default select-none">
                  {isCurrentFreePlan ? 'Current Plan' : 'Included in Premium'}
                </div>
              ) : !user ? (
                <Link
                  href={freeSignInHref}
                  onClick={() => {
                    trackEvent('cta_clicked', {
                      cta_location: 'pricing_free_card',
                      cta_text: 'Start free, no card',
                      billing_interval: billingInterval,
                    })
                  }}
                  className="mt-6 inline-flex w-full items-center justify-center min-h-[44px] px-5 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold text-sm hover:border-gray-400 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                >
                  Start free, no card
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={(event) => handleSubscribe('Free', event)}
                  disabled={freeButtonDisabled}
                  className="mt-6 inline-flex w-full items-center justify-center min-h-[44px] px-5 py-2 rounded-xl text-gray-500 font-medium text-sm hover:text-gray-700 hover:underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
                >
                  {loadingPlan === 'Free' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Continue with free'
                  )}
                </button>
              )}
            </HoverLift>
          </Stagger.Item>

          {/* Premium tier — promoted: stronger border, popular badge, gradient CTA */}
          <Stagger.Item>
            <HoverLift
              lift={-4}
              pressScale={1}
              className="relative rounded-[20px] border-2 border-green-500 p-6 md:p-7 flex flex-col h-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.72) 64%, rgba(240,253,244,0.8) 100%)',
                backdropFilter: 'blur(16px) saturate(140%)',
                WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                boxShadow:
                  '0 20px 50px -14px rgba(22,101,52,0.20), 0 12px 40px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)',
              }}
            >
              <PopularBadge />
              <h2 className="text-lg font-bold text-gray-900">Premium</h2>
              <p className="mt-1 text-xs text-gray-500">
                Everything in Free, plus what actually saves your lawn
              </p>

              <div className="mt-3 flex items-baseline">
                <span className="text-[46px] leading-none font-extrabold text-gray-900">
                  {premiumPriceBlock.big}
                </span>
                <span className="ml-1.5 text-sm font-medium text-gray-500">
                  {premiumPriceBlock.suffix}
                </span>
              </div>
              {/* Keyed on the interval so the price line re-settles (fade + tiny
                  rise) each time the toggle flips — the price is the thing the
                  user is deciding on, so it should react to the toggle (Mobbin
                  Visitors paywall). The "Free for N days" hero above stays put
                  by design; only the per-interval price sub-line animates. */}
              <div
                key={isYearly ? 'yearly' : 'monthly'}
                className="mt-1 text-xs text-gray-600 font-medium animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none"
              >
                {premiumPriceBlock.sub}
              </div>

              {/* The App Store price is the anchor the web price is being
                  compared against, so it is struck through rather than stated
                  plainly. It renders only on an interval that actually carries
                  the coupon. The reason line is not decoration: an unexplained
                  discount reads as introductory, and buyers price in a future
                  increase. Naming the App Store fee makes it a standing
                  difference. The transfer line answers the objection a
                  two-platform product creates and holds on both intervals. */}
              <div className="mt-2">
                {premiumPlan.active && (
                  <>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-xs font-medium text-gray-400 line-through">
                        {premiumPriceBlock.appStoreAnchor}
                      </span>
                      <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                        Save {premiumPlan.label} buying here
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">
                      {WEB_DISCOUNT_REASON}
                    </p>
                  </>
                )}
                <p className="mt-1 text-[11px] leading-snug text-gray-500">
                  {PREMIUM_TRANSFERS_TO_APP}
                </p>
              </div>

              <PremiumTrialTimeline
                trialDays={PREMIUM_TRIAL_DAYS}
                renewalPriceLabel={renewalPriceLabel}
                className="mt-5"
              />

              <ul className="mt-5 space-y-2 flex-1">
                {PREMIUM_FEATURES.map((feature) => (
                  <li
                    key={feature.text}
                    className={`flex items-start gap-2 text-sm ${
                      feature.highlight ? '-mx-2 px-2 py-1 rounded-md bg-green-100/70' : ''
                    }`}
                  >
                    <Check
                      className={`w-4 h-4 shrink-0 mt-0.5 ${
                        feature.highlight ? 'text-green-700' : 'text-green-600'
                      }`}
                    />
                    <span
                      className={
                        feature.highlight
                          ? 'font-semibold text-gray-900'
                          : 'text-gray-700'
                      }
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={(event) => handleSubscribe('Premium', event)}
                disabled={premiumButtonDisabled}
                className="mt-6 inline-flex w-full items-center justify-center min-h-[52px] px-5 py-3 rounded-full bg-linear-to-r from-green-700 to-emerald-700 hover:from-green-800 hover:to-emerald-800 text-white font-bold text-base tracking-[-0.01em] border border-white/35 shadow-[0_10px_24px_-6px_rgba(22,163,74,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] transition-[background,box-shadow,transform] duration-200 hover:-translate-y-px hover:shadow-[0_16px_32px_-6px_rgba(22,163,74,0.55),inset_0_1px_0_rgba(255,255,255,0.4)] active:scale-[0.985] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isCheckoutLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {loadingPortal ? 'Opening billing...' : 'Starting checkout...'}
                  </>
                ) : isPremium ? (
                  'Manage subscription'
                ) : (
                  premiumCtaLabel
                )}
              </button>

              {/* Price + cancel terms coupled to the action. Audible-pattern
                  lever (lazyweb paywall-CTA research): stating the post-trial
                  renewal price and cancel-anytime *beside the button* reduces
                  uncertainty at click-decision time without sending users to
                  legal copy. The icon triplet below carries the risk-reversal
                  glyphs; this single line carries the number the CTA omits. */}
              {!isPremium && (
                <p className="mt-2.5 text-center text-xs text-gray-600">
                  {isYearly
                    ? `Free for ${PREMIUM_TRIAL_DAYS} days, then ${WEB_DISCOUNT.yearly.webDisplay}. Cancel anytime.`
                    : `Free for ${PREMIUM_TRIAL_DAYS} days, then ${WEB_DISCOUNT.monthly.webDisplay}. Cancel anytime.`}
                </p>
              )}

              {/* In-card risk-reversal triplet — matches homepage's PricingPreview.
                  Replaces the standalone trust-pill row that used to live above
                  the cards (now removed for visual continuity). */}
              {!isPremium && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <Shield className="w-3 h-3 text-green-600" aria-hidden="true" />
                    7-day money back
                  </span>
                  <span aria-hidden="true" className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <RotateCcw className="w-3 h-3 text-green-600" aria-hidden="true" />
                    Cancel anytime
                  </span>
                  <span aria-hidden="true" className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <CreditCard className="w-3 h-3 text-green-600" aria-hidden="true" />
                    No charge for {PREMIUM_TRIAL_DAYS} days
                  </span>
                </div>
              )}

              {isPremium && (
                <p className="mt-3 text-center text-xs text-gray-500">
                  Manage payment method, invoices, cancellation, and plan changes in Stripe.
                </p>
              )}
            </HoverLift>
          </Stagger.Item>
        </Stagger>
      </div>
        </div>
      </section>

      {/* Keep this value bridge factual. Customer quotes only return here once
          we have permissioned, attributable testimonials. */}
      <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <Reveal className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-[-0.02em]">
              One plan that follows your lawn through the season
            </h2>
            <p className="mt-3 text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Premium connects your grass, location, diagnoses, and completed work so the next recommendation starts with what you already know.
            </p>
          </Reveal>

          <Reveal className="grid md:grid-cols-3 gap-5">
            {[
              ['Know what to do next', 'A 12-month care plan adjusts recommendations to your grass and ZIP code.'],
              ['Keep your lawn history', 'Save analyses, diagnoses, treatments, and notes in one account.'],
              ['Get timing reminders', 'Receive reminders for the care windows in your plan instead of relying on a generic calendar.'],
            ].map(([title, description]) => (
              <section
                key={title}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-5"
              >
                <Check className="h-5 w-5 text-green-600" aria-hidden="true" />
                <h3 className="mt-3 font-semibold text-gray-900 dark:text-white">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{description}</p>
              </section>
            ))}
          </Reveal>
        </div>
      </div>

      {/* Qualitative comparison strip. Avoid unsourced competitor pricing or
          savings claims; only compare the workflow features offered here. */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <Reveal className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 tracking-[-0.02em]">
              How we stack up
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
              Four ways to figure out your lawn. One that&apos;s built for it.
            </p>
          </Reveal>

          {/* Desktop table + mobile compact card via ResponsiveTable primitive */}
          <ResponsiveTable breakpoint="md">
            <ResponsiveTable.Desktop>
              <Reveal>
                <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
                  <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-5 py-4 font-semibold text-gray-700 dark:text-gray-300"></th>
                    <th className="text-center px-3 py-4 font-medium text-gray-600 dark:text-gray-400">
                      DIY guessing
                    </th>
                    <th className="text-center px-3 py-4 font-medium text-gray-600 dark:text-gray-400">
                      Big-box advice
                    </th>
                    <th className="text-center px-3 py-4 font-medium text-gray-600 dark:text-gray-400">
                      Landscaper
                    </th>
                    <th className="text-center px-3 py-4 font-bold text-green-700 dark:text-green-300 bg-green-50/60 dark:bg-green-900/20">
                      WhatGrassIsThis
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Personalized to your zip', diy: false, big: false, ls: true, us: true },
                    { label: 'Confidence score on grass ID', diy: false, big: false, ls: 'partial', us: true },
                    { label: 'Smart timing reminders', diy: false, big: false, ls: 'partial', us: true },
                    { label: '12-month written care plan', diy: false, big: false, ls: true, us: true },
                    { label: 'Problem diagnosis on demand', diy: false, big: false, ls: 'partial', us: true },
                    { label: 'Always available, 24/7', diy: true, big: true, ls: false, us: true },
                  ].map((row, idx) => (
                    <tr key={row.label} className={idx % 2 === 0 ? 'bg-gray-50/40 dark:bg-gray-800/20' : ''}>
                      <td className="px-5 py-3 text-gray-900 dark:text-white">{row.label}</td>
                      {(['diy', 'big', 'ls', 'us'] as const).map((col) => {
                        const v = row[col]
                        const isUs = col === 'us'
                        return (
                          <td
                            key={col}
                            className={`text-center px-3 py-3 ${isUs ? 'bg-green-50/60 dark:bg-green-900/20' : ''}`}
                          >
                            {v === true ? (
                              <Check className={`inline h-5 w-5 ${isUs ? 'text-green-700 dark:text-green-300' : 'text-green-600 dark:text-green-400'}`} aria-label="Yes" />
                            ) : v === 'partial' ? (
                              <Minus className="inline h-5 w-5 text-amber-500" aria-label="Sometimes" />
                            ) : (
                              <XIcon className="inline h-5 w-5 text-gray-300 dark:text-gray-600" aria-label="No" />
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
                  </table>
                </div>
              </Reveal>
            </ResponsiveTable.Desktop>

            <ResponsiveTable.Mobile>
              <Reveal className="rounded-2xl border-2 border-green-500 bg-white dark:bg-gray-900 p-5 shadow-sm">
            <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="font-bold text-gray-900 dark:text-white">WhatGrassIsThis</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Complete Premium plan</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-green-700 dark:text-green-300">
                  {isYearly
                    ? `$${YEARLY_PRICE.toFixed(2)}/yr`
                    : `$${MONTHLY_PRICE.toFixed(2)}/mo`}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400">Cancel anytime</div>
              </div>
            </div>
            <ul className="space-y-2.5 text-sm text-gray-900 dark:text-white">
              {[
                'Personalized to your zip',
                'Confidence score on grass ID',
                'Smart timing reminders',
                '12-month written care plan',
                'Problem diagnosis on demand',
                'Always available, 24/7',
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                  <span>{line}</span>
                </li>
              ))}
              </ul>
            </Reveal>
            </ResponsiveTable.Mobile>
          </ResponsiveTable>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="reading-rail py-16">
        <Reveal>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
            Frequently asked questions
          </h2>
        </Reveal>

        {/* Accordion FAQ (Radix). Each
            <Item value> must be unique on the page. */}
        <Reveal>
        <AccordionPrimitive.Root
          type="single"
          collapsible
          defaultValue="How do I know Premium is worth it for me?"
          className="divide-y divide-gray-200 dark:divide-gray-700"
        >
          {[
            {
              q: 'How do I know Premium is worth it for me?',
              a: `Free is a good fit for occasional analysis and calculators. Premium is for people who want one 12-month plan, saved lawn history, unlimited analyses, and reminders tied to their grass and ZIP code. The ${PREMIUM_TRIAL_DAYS}-day trial lets you try the complete workflow before deciding.`,
            },
            {
              q: `How does the ${PREMIUM_TRIAL_DAYS}-day trial work?`,
              a: `New subscribers on either plan get the first ${PREMIUM_TRIAL_DAYS} days on us. We collect a card at signup but do not charge it until the trial ends. Cancel any time before then from your dashboard and you will not be billed.`,
            },
            {
              q: 'Can I cancel my subscription anytime?',
              a: 'Yes. You can cancel your subscription at any time from your dashboard. You’ll continue to have access to premium features until the end of your billing period.',
            },
            {
              q: 'Do you offer refunds?',
              a: 'For web subscriptions, request a full refund within 7 days of your first paid charge by contacting support. The free trial is not a paid charge. App Store purchases follow Apple’s refund policies.',
            },
            {
              q: 'Does my subscription work on both web and the iPhone app?',
              a: 'Yes, in both directions. Premium follows your account, not the platform you bought it on. Subscribe here and sign in to the iPhone app with the same account to unlock it there. Subscribe in the app and Premium is already waiting when you sign in on the web.',
            },
            {
              q: 'Is there an iPhone app?',
              a: 'Yes. The What Grass Is This? iPhone app is available now, and your account keeps your lawn information connected across web and iPhone. Android is not currently available.',
            },
          ].map((faq) => (
            <AccordionPrimitive.Item key={faq.q} value={faq.q}>
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger className="group flex flex-1 items-center justify-between gap-4 py-4 text-left text-base sm:text-lg font-semibold text-gray-900 dark:text-white hover:text-green-700 dark:hover:text-green-300 transition-colors">
                  <span>{faq.q}</span>
                  <FaqLeafChevron className="w-4 h-4 shrink-0 text-green-600 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="overflow-hidden text-sm sm:text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <p className="pb-4">{faq.a}</p>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>
        </Reveal>

        <Reveal className="text-center mt-12">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Still have questions?
          </p>
          <Link
            href="/faq"
            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium"
          >
            View all FAQs →
          </Link>
        </Reveal>
      </div>

      {/* Final CTA section — captures end-of-page scrollers who read every
          objection and need one more nudge. Outcome-driven CTA + risk
          reversal stack. Mirrors the homepage's FinalCta gradient pattern. */}
      <div
        className="relative overflow-hidden border-t border-gray-200 dark:border-gray-800 px-7 py-16 md:py-20"
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, #dcfce7 100%)' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-56 right-[6%] h-[420px] w-[420px] rounded-full bg-amber-400/15 blur-[80px]"
        />
        <Reveal className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="font-poppins font-extrabold tracking-[-0.03em] text-gray-900 mb-4 text-[28px] md:text-4xl">
            Stop guessing. Start growing.
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 text-base md:text-lg">
            Build a care plan around your grass and ZIP code, then keep every recommendation and reminder in one place.
          </p>
          <Button
            onClick={() => {
              trackEvent('cta_clicked', {
                cta_location: 'pricing_page_final_cta',
                cta_text: `Start my ${PREMIUM_TRIAL_DAYS}-day trial`,
                billing_interval: billingInterval,
              })
              pricingCardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            size="lg"
            className="inline-flex h-auto max-w-full items-center justify-center gap-2 whitespace-normal px-6 py-4 text-center rounded-full bg-linear-to-r from-green-700 to-emerald-700 hover:from-green-800 hover:to-emerald-800 text-white font-bold text-lg border border-white/35 shadow-[0_12px_32px_rgba(22,163,74,0.28),inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.985] min-h-[56px]"
          >
            <Zap className="w-5 h-5" />
            Start my {PREMIUM_TRIAL_DAYS}-day trial
          </Button>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Shield className="w-3 h-3 text-green-600" aria-hidden="true" />
              7-day money back
            </span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3 text-green-600" aria-hidden="true" />
              Cancel anytime
            </span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <CreditCard className="w-3 h-3 text-green-600" aria-hidden="true" />
              {paymentTimingLabel}
            </span>
          </div>
        </Reveal>
      </div>

    </div>
  )
}
